import { useMemo } from 'react';

/**
 * ConnectionSourceLogo — the brand lockup used across the app.
 * Concept #9: Tower + Wordmark + Animated Data Pulse.
 *
 * Props:
 *   size      — 'sm' | 'md' | 'lg' (default 'md')
 *   showPulse — whether to render the horizontal heartbeat line (default true for md/lg, false for sm)
 *   stacked   — if true, "CONNECTION" on top / "SOURCE" underneath (login/splash). Default inline.
 *   compact   — icon-only (for favicon/tight navbars)
 */
export default function ConnectionSourceLogo({
  size = 'md',
  showPulse,
  stacked = false,
  compact = false,
  className = '',
}) {
  const cfg = useMemo(() => {
    const map = {
      sm: { iconSize: 22, fontSize: 14,   pulseH: 10, gap: 8,  stackFont: 18 },
      md: { iconSize: 30, fontSize: 20,   pulseH: 14, gap: 10, stackFont: 28 },
      lg: { iconSize: 64, fontSize: 48,   pulseH: 28, gap: 16, stackFont: 56 },
    };
    return map[size] || map.md;
  }, [size]);

  const shouldShowPulse = showPulse ?? (size !== 'sm' && !compact);

  return (
    <div
      className={`cs-logo ${stacked ? 'cs-logo-stacked' : 'cs-logo-inline'} ${className}`}
      style={{ '--cs-logo-gap': `${cfg.gap}px` }}
      data-testid="cs-logo"
    >
      {/* Tower icon */}
      <ConnectionSourceTower size={cfg.iconSize} />

      {!compact && (
        <div className="cs-logo-text-wrap">
          {stacked ? (
            <>
              <span className="cs-logo-word cs-logo-word-primary" style={{ fontSize: `${cfg.stackFont}px`, lineHeight: 0.95 }}>Connection</span>
              <span className="cs-logo-word cs-logo-word-accent"  style={{ fontSize: `${cfg.stackFont}px`, lineHeight: 0.95 }}>Source</span>
              {shouldShowPulse && <DataPulse height={cfg.pulseH} stacked />}
            </>
          ) : (
            <>
              <span className="cs-logo-wordmark" style={{ fontSize: `${cfg.fontSize}px` }}>
                <span className="cs-logo-word-primary">Connection</span>
                <span className="cs-logo-word-space">&nbsp;</span>
                <span className="cs-logo-word-accent">Source</span>
              </span>
              {shouldShowPulse && <DataPulse height={cfg.pulseH} />}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Tower SVG with breathing signal arcs ── */
function ConnectionSourceTower({ size = 30 }) {
  return (
    <svg
      className="cs-logo-tower"
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      aria-hidden="true"
    >
      {/* Base pedestal */}
      <rect x="18" y="40" width="12" height="3" rx="0.5" className="cs-tower-base" />
      {/* Tower body — converging truss lines */}
      <path d="M20 40 L22 18 L26 18 L28 40" className="cs-tower-body" />
      <path d="M20 40 L28 40" className="cs-tower-body" />
      {/* Cross braces */}
      <line x1="20.7" y1="32" x2="27.3" y2="32" className="cs-tower-brace" />
      <line x1="21.2" y1="26" x2="26.8" y2="26" className="cs-tower-brace" />
      <line x1="20.7" y1="32" x2="26.8" y2="26" className="cs-tower-brace" />
      <line x1="27.3" y1="32" x2="21.2" y2="26" className="cs-tower-brace" />
      {/* Antenna */}
      <line x1="24" y1="18" x2="24" y2="11" className="cs-tower-antenna" />
      {/* Hub dot at top of antenna */}
      <circle cx="24" cy="11" r="1.8" className="cs-tower-hub" />
      {/* Signal arcs — left side (3 rings, staggered) */}
      <path d="M 20 11 Q 17 11, 17 8" className="cs-tower-arc cs-tower-arc-1" />
      <path d="M 18 11 Q 14 11, 14 6" className="cs-tower-arc cs-tower-arc-2" />
      <path d="M 16 11 Q 11 11, 11 4" className="cs-tower-arc cs-tower-arc-3" />
      {/* Signal arcs — right side (mirrored) */}
      <path d="M 28 11 Q 31 11, 31 8" className="cs-tower-arc cs-tower-arc-1" />
      <path d="M 30 11 Q 34 11, 34 6" className="cs-tower-arc cs-tower-arc-2" />
      <path d="M 32 11 Q 37 11, 37 4" className="cs-tower-arc cs-tower-arc-3" />
    </svg>
  );
}

/* ── Animated data pulse (heartbeat monitor) ── */
function DataPulse({ height = 14, stacked = false }) {
  return (
    <svg
      className={`cs-logo-pulse ${stacked ? 'cs-logo-pulse-stacked' : ''}`}
      width="100%"
      height={height}
      viewBox="0 0 220 14"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="cs-pulse-grad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%"   stopColor="var(--cs-pulse-start, #CD040B)" stopOpacity="0" />
          <stop offset="50%"  stopColor="var(--cs-pulse-mid,   #CD040B)" stopOpacity="1" />
          <stop offset="100%" stopColor="var(--cs-pulse-end,   #CD040B)" stopOpacity="0" />
        </linearGradient>
      </defs>
      {/* Baseline */}
      <line x1="0" y1="7" x2="220" y2="7" className="cs-pulse-baseline" />
      {/* ECG trace */}
      <polyline
        points="0,7 60,7 70,7 74,2 80,12 86,3 92,10 96,7 140,7 148,4 154,10 160,7 220,7"
        className="cs-pulse-trace"
        stroke="url(#cs-pulse-grad)"
      />
      {/* Moving dot along the trace */}
      <circle r="1.8" cy="7" className="cs-pulse-dot" />
    </svg>
  );
}
